import {
  formatProductUsdPrice,
  getProductPerUnitLine,
} from "./productTradeSpecs";

export interface BizcoverProductListCardData {
  id: string;
  title: string;
  price: string;
  unitPrice?: string;
  subtitle?: string;
  subtitleVerified?: boolean;
  imageUrl?: string;
}

const applyDiscount = (base: number, product: Record<string, unknown>): number => {
  const pct = product.discount != null ? Number(product.discount) : 0;
  if (Number.isFinite(pct) && pct > 0) {
    return base * (1 - pct / 100);
  }
  return base;
};

const formatListCardPrice = (product: Record<string, unknown>): string => {
  const priceMin = product.priceMin != null ? Number(product.priceMin) : null;
  const priceMax = product.priceMax != null ? Number(product.priceMax) : null;

  if (priceMin != null && priceMax != null && Number.isFinite(priceMin) && Number.isFinite(priceMax)) {
    const min = applyDiscount(priceMin, product);
    const max = applyDiscount(priceMax, product);
    return `${formatProductUsdPrice(min)} - ${formatProductUsdPrice(max)}`;
  }

  const base = Number(product.price ?? product.priceMin ?? 0) || 0;
  const display = applyDiscount(base, product);
  return display > 0 ? formatProductUsdPrice(display) : "Price on request";
};

const getProductImage = (product: Record<string, unknown>): string => {
  const image =
    product.image ||
    (Array.isArray(product.images) ? product.images[0] : undefined) ||
    product.imageUrl ||
    product.thumbnailImage ||
    "";
  return typeof image === "string" ? image : "";
};

const getProductSubtitle = (
  product: Record<string, unknown>,
  supplierName?: string,
): string => {
  const category = product.category as Record<string, unknown> | undefined;
  const categoryRelation = product.categoryRelation as Record<string, unknown> | undefined;
  const categoryName =
    (typeof category?.name === "string" && category.name.trim()) ||
    (typeof categoryRelation?.name === "string" && categoryRelation.name.trim()) ||
    (typeof product.categoryName === "string" && product.categoryName.trim()) ||
    "";

  if (categoryName) return categoryName;
  if (supplierName?.trim()) return supplierName.trim();

  const moq = product.moq;
  const moqUnit = product.moqUnit || product.unitOfMeasure || "pieces";
  if (moq != null && String(moq).trim()) {
    return `MOQ: ${moq} ${moqUnit}`;
  }

  return "";
};

export function mapProductToListCard(
  product: Record<string, unknown>,
  options?: { supplierName?: string },
): BizcoverProductListCardData {
  const unitLine = getProductPerUnitLine(product);
  const approvalStatus = String(product.approvalStatus || "").toLowerCase();

  return {
    id: String(product.id || ""),
    title: String(product.name || product.title || "Product"),
    price: formatListCardPrice(product),
    unitPrice: unitLine || undefined,
    subtitle: getProductSubtitle(product, options?.supplierName),
    subtitleVerified: approvalStatus === "approved",
    imageUrl: getProductImage(product) || undefined,
  };
}

export function mapProductsToListCard(
  products: Record<string, unknown>[],
  options?: { supplierName?: string },
): BizcoverProductListCardData[] {
  return products.map((product) => mapProductToListCard(product, options));
}
